import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { Button } from "../components/ui/button";
import RelatedSolutionsStrip from "../components/blog/RelatedSolutionsStrip";
import {
    getLocalizedBlogPosts,
    getLocalizedPostBySlug,
} from "../data/blogMultilingual";
import { useLanguage } from "../contexts/LanguageContext";
import { useDbBlogPost, useDbBlogPosts } from "../hooks/useDbBlogPosts";
import { localizedPath } from "../lib/locale";

const SITE_URL = "https://adrise-agency.com";

export default function BlogPostPage() {
    const { language } = useLanguage();
    const { slug } = useParams();
    const dbPosts = useDbBlogPosts(language);
    const staticPost = getLocalizedPostBySlug(slug, language);
    const { post: dbPost, loading } = useDbBlogPost(slug, Boolean(staticPost), language);
    const posts = [...dbPosts, ...getLocalizedBlogPosts(language)];
    const post = staticPost || dbPost;

    if (!post && loading) {
        return <div className="min-h-screen bg-background" data-testid="blog-post-loading" />;
    }
    if (!post) return <Navigate to={localizedPath(language, "/blog")} replace />;

    const related = posts.filter(
        (p) => p.slug !== slug && p.category === post.category,
    ).slice(0, 3);
    const fillRelated =
        related.length < 3
            ? [
                  ...related,
                  ...posts.filter(
                      (p) =>
                          p.slug !== slug &&
                          !related.some((r) => r.slug === p.slug),
                  ).slice(0, 3 - related.length),
              ]
            : related;
    const c = {
        en: { allResources: "All resources", getAccess: "Get whitelisted access", applyLine: "Apply for Adrise Credit Line", applyDesc: "Platinum HIVA Partner accounts. Crypto-only funding. Reviewed within 24h.", applyBtn: "Apply for Access", topics: "Topics", keepReading: "Keep reading", more: "More playbooks for serious operators." },
        pt: { allResources: "Todos os recursos", getAccess: "Acesso whitelisted", applyLine: "Solicitar Credit Line da Adrise", applyDesc: "Contas Platinum HIVA Partner. Funding em cripto. Revisão em até 24h.", applyBtn: "Solicitar acesso", topics: "Tópicos", keepReading: "Continue lendo", more: "Mais playbooks para operadores sérios." },
        es: { allResources: "Todos los recursos", getAccess: "Acceso whitelisted", applyLine: "Solicitar Credit Line de Adrise", applyDesc: "Cuentas Platinum HIVA Partner. Fondeo en cripto. Revisión en 24h.", applyBtn: "Solicitar acceso", topics: "Temas", keepReading: "Sigue leyendo", more: "Más playbooks para operadores serios." },
        ru: { allResources: "Все материалы", getAccess: "Whitelisted доступ", applyLine: "Подать заявку на Credit Line Adrise", applyDesc: "Аккаунты Platinum HIVA Partner. Крипто-фандинг. Проверка до 24 часов.", applyBtn: "Подать заявку", topics: "Темы", keepReading: "Читать дальше", more: "Больше плейбуков для серьёзных операторов." },
        tr: { allResources: "Tüm kaynaklar", getAccess: "Whitelisted erişim", applyLine: "Adrise Credit Line Başvurusu", applyDesc: "Platinum HIVA Partner hesapları. Sadece kripto fonlama. 24 saatte değerlendirme.", applyBtn: "Erişim Başvurusu", topics: "Konular", keepReading: "Okumaya devam et", more: "Ciddi operatörler için daha fazla playbook." },
    }[language] || {
        allResources: "All resources", getAccess: "Get whitelisted access", applyLine: "Apply for Adrise Credit Line", applyDesc: "Platinum HIVA Partner accounts. Crypto-only funding. Reviewed within 24h.", applyBtn: "Apply for Access", topics: "Topics", keepReading: "Keep reading", more: "More playbooks for serious operators.",
    };
    const dateLocale = language === "pt" ? "pt-BR" : language === "es" ? "es-ES" : language === "ru" ? "ru-RU" : language === "tr" ? "tr-TR" : "en-US";
    const postUrl = `${SITE_URL}${localizedPath(language, `/blog/${post.slug}`)}`;

    const breadcrumbJsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
            { "@type": "ListItem", position: 3, name: post.title, item: postUrl },
        ],
    };

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headrise: post.title,
        description: post.description,
        datePublished: post.date,
        dateModified: post.date,
        url: postUrl,
        inLanguage: language,
        image: post.cover?.startsWith("/") ? `${SITE_URL}${post.cover}` : post.cover,
        author: { "@type": "Organization", name: post.author, url: SITE_URL },
        publisher: {
            "@type": "Organization",
            name: "Adrise",
            url: SITE_URL,
            logo: {
                "@type": "ImageObject",
                url: `${SITE_URL}/logo.png`,
            },
        },
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": postUrl,
        },
        keywords: post.keywords?.join(", "),
    };

    return (
        <main
            data-testid={`blog-post-${slug}`}
            className="bg-background text-foreground min-h-screen"
        >
            <SEOHead
                title={`${post.title} | Adrise`}
                description={post.description}
                canonical={`${SITE_URL}/blog/${post.slug}`}
                image={post.cover}
                type="article"
                keywords={post.keywords || []}
                article={{
                    publishedTime: post.date,
                    author: post.author,
                    section: post.category,
                }}
                jsonLd={[jsonLd, breadcrumbJsonLd]}
            />

            <Navbar />

            <article className="relative pt-28 md:pt-36 pb-20 md:pb-28">
                <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
                <div className="absolute inset-0 noise pointer-events-none" />

                <div className="container-skala relative">
                    {/* Back link */}
                    <Link
                        to={localizedPath(language, "/blog")}
                        data-testid="blog-back-link"
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-10"
                    >
                        <ArrowLeft className="w-4 h-4" /> {c.allResources}
                    </Link>

                    {/* Header */}
                    <header className="max-w-3xl space-y-6 mb-12">
                        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.2em]">
                            <span className="text-primary">
                                {post.category}
                            </span>
                            <span className="text-muted-foreground inline-flex items-center gap-1.5">
                                <Calendar className="w-3 h-3" />
                                {new Date(post.date).toLocaleDateString(
                                    dateLocale,
                                    {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    },
                                )}
                            </span>
                            <span className="text-muted-foreground inline-flex items-center gap-1.5">
                                <Clock className="w-3 h-3" />
                                {post.reading_time}
                            </span>
                        </div>
                        <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            {post.title}
                        </h1>
                        <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                            {post.excerpt}
                        </p>
                    </header>

                    {/* Cover */}
                    <div className="rounded-3xl overflow-hidden mb-14 aspect-[16/8] max-w-5xl">
                        <img
                            src={post.cover}
                            alt={post.title}
                            className="w-full h-full object-cover"
                        />
                    </div>

                    {/* Body */}
                    <div className="grid lg:grid-cols-12 gap-10">
                        <div
                            data-testid="blog-post-body"
                            className="lg:col-span-8 prose-skala"
                            dangerouslySetInnerHTML={{
                                __html: post.content_html,
                            }}
                        />

                        {/* Sidebar */}
                        <aside className="lg:col-span-4 space-y-5 lg:sticky lg:top-28 self-start">
                            <div className="bg-card border border-primary/30 rounded-2xl p-6 glow-primary">
                                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary mb-3">
                                    {c.getAccess}
                                </p>
                                <h3 className="font-display font-bold text-xl mb-2">
                                    {c.applyLine}
                                </h3>
                                <p className="text-sm text-muted-foreground mb-5">
                                    {c.applyDesc}
                                </p>
                                <Link
                                    to="/signup"
                                    data-testid="blog-sidebar-cta"
                                >
                                    <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11">
                                        {c.applyBtn}
                                    </Button>
                                </Link>
                            </div>

                            {post.keywords?.length > 0 && (
                                <div className="bg-card border border-white/[0.08] rounded-2xl p-6">
                                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground mb-3">
                                        {c.topics}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {post.keywords.map((k) => (
                                            <span
                                                key={k}
                                                className="text-xs bg-white/[0.03] border border-white/10 rounded-full px-2.5 py-1 text-foreground/85"
                                            >
                                                {k}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </aside>
                    </div>
                </div>
            </article>

            <RelatedSolutionsStrip />

            {/* Related */}
            <section className="py-20 md:py-24 border-t border-white/5 bg-white/[0.01]">
                <div className="container-skala">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary mb-2">
                                {c.keepReading}
                            </p>
                            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight">
                                {c.more}
                            </h2>
                        </div>
                        <Link
                            to={localizedPath(language, "/blog")}
                            className="text-sm text-foreground/85 hover:text-primary inline-flex items-center gap-1.5"
                        >
                            {c.allResources} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5">
                        {fillRelated.map((p) => (
                            <Link
                                key={p.slug}
                                to={localizedPath(language, `/blog/${p.slug}`)}
                                data-testid={`blog-related-${p.slug}`}
                                className="group bg-card border border-white/[0.07] hover:border-primary/40 rounded-2xl overflow-hidden transition-colors"
                            >
                                <div className="aspect-[16/10] bg-white/5 overflow-hidden">
                                    <img
                                        src={p.cover}
                                        alt={p.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div className="p-5 space-y-2">
                                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">
                                        {p.category}
                                    </div>
                                    <h3 className="font-display font-semibold text-lg leading-snug group-hover:text-primary transition-colors">
                                        {p.title}
                                    </h3>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <FloatingContact />
            <Footer />
        </main>
    );
}
